'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { mediaCache } from '@/lib/media-cache';
import { preloadPageMedia, preloadMultiplePagesMedia, getCachedPageMedia } from '@/lib/preload-page-media';
import { getPagesToPreload } from '@/lib/page-priority';
import { PageMediaResponse } from '@/lib/graphql/schema';

interface VideoRegistration {
  sectionId: string;
  videoUrlMobile?: string;
  videoUrlDesktop?: string;
  isVideo: boolean;
}

interface MediaContextType {
  // Video loading state (merged from VideoLoadingContext)
  videosLoaded: boolean;
  setVideosLoaded: (loaded: boolean) => void;
  
  // Video background registration (merged from VideoBackgroundContext)
  registeredVideos: Map<string, VideoRegistration>;
  activeSectionId: string | null;
  registerVideo: (sectionId: string, videoUrlMobile: string | undefined, videoUrlDesktop: string | undefined, isVideo: boolean) => void;
  unregisterVideo: (sectionId: string) => void;
  setActiveSection: (sectionId: string | null | ((current: string | null) => string | null)) => void;
  
  // Unified media cache methods
  isMediaPreloaded: (url: string) => boolean;
  getCacheStats: () => { cached: number; preloading: number; browserCached: number; memoryCacheSize: number };
  
  // GraphQL-based page media prefetching
  prefetchPageMedia: (slug: string) => Promise<void>;
  prefetchNavigationMedia: () => Promise<void>;
  getPageMedia: (slug: string) => PageMediaResponse | null;
  currentPageSlug: string | null;
  setCurrentPageSlug: (slug: string | null) => void;
  
  // Page loading state tracking
  isPageMediaLoaded: (slug: string) => boolean;
  markPageLoaded: (slug: string) => void;
}

const MediaContext = createContext<MediaContextType | undefined>(undefined);

interface LoadedPageState {
  slug: string;
  mediaLoaded: boolean;
  timestamp: number;
}

const LOADED_PAGES_KEY = 'evoque-loaded-pages-v1';

export function MediaProvider({ children }: { children: ReactNode }) {
  const [videosLoaded, setVideosLoaded] = useState(false);
  const [registeredVideos, setRegisteredVideos] = useState<Map<string, VideoRegistration>>(new Map());
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [currentPageSlug, setCurrentPageSlug] = useState<string | null>(null);
  const [navigationPrefetched, setNavigationPrefetched] = useState(false);
  const [loadedPages, setLoadedPages] = useState<Map<string, LoadedPageState>>(new Map());

  // Load persisted loaded pages from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined' || !('localStorage' in window)) {
      return;
    }

    try {
      const stored = localStorage.getItem(LOADED_PAGES_KEY);
      if (stored) {
        const pages: LoadedPageState[] = JSON.parse(stored);
        const pagesMap = new Map<string, LoadedPageState>();
        for (const page of pages) {
          pagesMap.set(page.slug, page);
        }
        setLoadedPages(pagesMap);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[MediaContext] Failed to load persisted pages:', error);
      }
    }
  }, []);

  // Save loaded pages to localStorage
  const saveLoadedPages = useCallback((pages: Map<string, LoadedPageState>) => {
    if (typeof window === 'undefined' || !('localStorage' in window)) {
      return;
    }

    try {
      const pagesArray = Array.from(pages.values());
      // Only keep recent pages (last 50) to avoid localStorage size limits
      const recentPages = pagesArray
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 50);
      localStorage.setItem(LOADED_PAGES_KEY, JSON.stringify(recentPages));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[MediaContext] Failed to save loaded pages:', error);
      }
    }
  }, []);

  // Mark a page as loaded
  const markPageLoaded = useCallback((slug: string) => {
    setLoadedPages((prev) => {
      const newMap = new Map(prev);
      newMap.set(slug, {
        slug,
        mediaLoaded: true,
        timestamp: Date.now(),
      });
      saveLoadedPages(newMap);
      return newMap;
    });
  }, [saveLoadedPages]);

  // Check if a page's media is already loaded
  const isPageMediaLoaded = useCallback((slug: string): boolean => {
    const page = loadedPages.get(slug);
    return page?.mediaLoaded ?? false;
  }, [loadedPages]);

  const registerVideo = useCallback((sectionId: string, videoUrlMobile: string | undefined, videoUrlDesktop: string | undefined, isVideo: boolean) => {
    setRegisteredVideos((prev) => {
      const newMap = new Map(prev);
      newMap.set(sectionId, { sectionId, videoUrlMobile, videoUrlDesktop, isVideo });
      return newMap;
    });
  }, []);

  const unregisterVideo = useCallback((sectionId: string) => {
    setRegisteredVideos((prev) => {
      const newMap = new Map(prev);
      newMap.delete(sectionId);
      return newMap;
    });
    // Clear active section if it's the one being unregistered
    setActiveSectionId((prev) => (prev === sectionId ? null : prev));
  }, []);

  const setActiveSection = useCallback((sectionId: string | null | ((current: string | null) => string | null)) => {
    setActiveSectionId(sectionId);
  }, []);

  const isMediaPreloaded = useCallback((url: string) => {
    return mediaCache.isPreloaded(url);
  }, []);

  const getCacheStats = useCallback(() => {
    return mediaCache.getStats();
  }, []);

  // Prefetch media for a specific page using GraphQL
  // Checks if page media is already loaded to prevent duplicate loading
  const prefetchPageMedia = useCallback(async (slug: string) => {
    // Skip if page media is already loaded
    if (isPageMediaLoaded(slug)) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[MediaContext] Page ${slug} media already loaded, skipping`);
      }
      return;
    }

    try {
      await preloadPageMedia(slug);
      // Mark page as loaded after successful preload
      markPageLoaded(slug);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[MediaContext] Error prefetching media for ${slug}:`, error);
      }
    }
  }, [isPageMediaLoaded, markPageLoaded]);

  // Prefetch media for all navigation pages
  const prefetchNavigationMedia = useCallback(async () => {
    if (navigationPrefetched) return;
    
    try {
      const pagesToPreload = getPagesToPreload(currentPageSlug || '');
      await preloadMultiplePagesMedia(pagesToPreload);
      setNavigationPrefetched(true);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[MediaContext] Error prefetching navigation media:', error);
      }
    }
  }, [currentPageSlug, navigationPrefetched]);

  // Get cached page media (synchronous)
  const getPageMedia = useCallback((slug: string): PageMediaResponse | null => {
    return getCachedPageMedia(slug);
  }, []);

  // Prefetch navigation media after current page loads
  useEffect(() => {
    if (!currentPageSlug || navigationPrefetched) return;

    // Wait for current page to load, then prefetch navigation
    const timeoutId = setTimeout(() => {
      prefetchNavigationMedia();
    }, 2000); // 2 second delay after page load

    return () => clearTimeout(timeoutId);
  }, [currentPageSlug, navigationPrefetched, prefetchNavigationMedia]);

  return (
    <MediaContext.Provider
      value={{
        videosLoaded,
        setVideosLoaded,
        registeredVideos,
        activeSectionId,
        registerVideo,
        unregisterVideo,
        setActiveSection,
        isMediaPreloaded,
        getCacheStats,
        prefetchPageMedia,
        prefetchNavigationMedia,
        getPageMedia,
        currentPageSlug,
        setCurrentPageSlug,
        isPageMediaLoaded,
        markPageLoaded,
      }}
    >
      {children}
    </MediaContext.Provider>
  );
}

// No-op functions for default context values
const noopSetVideosLoaded = (_loaded: boolean) => {
  // No-op: MediaProvider not available
};

const noopRegisterVideo = (_sectionId: string, _videoUrlMobile: string | undefined, _videoUrlDesktop: string | undefined, _isVideo: boolean) => {
  // No-op: MediaProvider not available
};

const noopUnregisterVideo = (_sectionId: string) => {
  // No-op: MediaProvider not available
};

const noopSetActiveSection = (_sectionId: string | null | ((current: string | null) => string | null)) => {
  // No-op: MediaProvider not available
};

const noopIsMediaPreloaded = (_url: string) => false;

const noopGetCacheStats = () => ({ cached: 0, preloading: 0, browserCached: 0, memoryCacheSize: 0 });

const noopPrefetchPageMedia = async (_slug: string) => {
  // No-op: MediaProvider not available
};

const noopPrefetchNavigationMedia = async () => {
  // No-op: MediaProvider not available
};

const noopGetPageMedia = (_slug: string): PageMediaResponse | null => null;

const noopSetCurrentPageSlug = (_slug: string | null) => {
  // No-op: MediaProvider not available
};

const noopIsPageMediaLoaded = (_slug: string) => false;
const noopMarkPageLoaded = (_slug: string) => {
  // No-op: MediaProvider not available
};

export function useMedia() {
  const context = useContext(MediaContext);
  if (!context) {
    // Return default values instead of throwing error for better resilience
    // This allows components to work even if MediaProvider is not available
    return {
      videosLoaded: false,
      setVideosLoaded: noopSetVideosLoaded,
      registeredVideos: new Map<string, VideoRegistration>(),
      activeSectionId: null,
      registerVideo: noopRegisterVideo,
      unregisterVideo: noopUnregisterVideo,
      setActiveSection: noopSetActiveSection,
      isMediaPreloaded: noopIsMediaPreloaded,
      getCacheStats: noopGetCacheStats,
      prefetchPageMedia: noopPrefetchPageMedia,
      prefetchNavigationMedia: noopPrefetchNavigationMedia,
      getPageMedia: noopGetPageMedia,
      currentPageSlug: null,
      setCurrentPageSlug: noopSetCurrentPageSlug,
      isPageMediaLoaded: noopIsPageMediaLoaded,
      markPageLoaded: noopMarkPageLoaded,
    };
  }
  return context;
}

// Backward compatibility hooks
export function useVideoLoading() {
  const { videosLoaded, setVideosLoaded } = useMedia();
  return { videosLoaded, setVideosLoaded };
}

export function useVideoBackground() {
  const { registeredVideos, activeSectionId, registerVideo, unregisterVideo, setActiveSection } = useMedia();
  return {
    registeredVideos,
    activeSectionId,
    registerVideo,
    unregisterVideo,
    setActiveSection,
  };
}

// New unified hooks
export function useMediaCache() {
  const { isMediaPreloaded, getCacheStats } = useMedia();
  return { isMediaPreloaded, getCacheStats };
}

export function usePreloadedMedia(url: string) {
  const { isMediaPreloaded } = useMedia();
  return isMediaPreloaded(url);
}

